import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, Checkbox, Select } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import Button from "@/components/common/Button";
import { CloseOutlined, SearchOutlined } from "@/config/antd";
import Input from "@/components/common/inputs/Input";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsersByAny } from "@/store/redux/slices/user_admin/user_admin";
import { assignRoleUsersAsync } from "@/store/redux/slices/roles/role_slice";
import { getOrganizationId } from "@/utils/token_functions";
import { createSelector } from "@reduxjs/toolkit";

const MemberCard = ({
  member,
  isSelected,
  onSelect,
  userRoleDisplay = "text",
  roleOptions = [],
  onRoleChange,
}) => (
  <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-[#E1F0FF] mb-3">
    <div className="flex items-center gap-4">
      <Avatar src={member.profileImage} size="large" shape="circle" />
      <div className="flex flex-col">
        <span className="text-regular font-semibold text-darkGrey">
          {member.fullname || member.username}
        </span>
        <span className="text-small text-gray-500">{member.email}</span>
      </div>
      {userRoleDisplay === "text" ? (
        <span className="text-primary text-small">{member.role}</span>
      ) : (
        <Select
          defaultValue={member.role || "User"}
          style={{ width: 120 }}
          onChange={(value) => onRoleChange(member._id, value)}
          options={roleOptions}
          size="small"
        />
      )}
    </div>
    <Checkbox checked={isSelected} onChange={() => onSelect(member._id)} />
  </div>
);

// Create memoized selector
const selectUserAdminState = createSelector(
  (state) => state.userAdmin,
  (userAdmin) => ({
    users:
      userAdmin.fetchedUsers?.length > 0
        ? userAdmin.fetchedUsers
        : userAdmin.users,
    isLoading: userAdmin.isLoading,
    total: userAdmin.total,
  })
);

const AddMoreUsersDialog = ({
  isVisible,
  onClose,
  role = null,
  assignedUserIds = [],
  onUsersAdded,
  modalTitle = "Add New User",
  primaryHeader = "CORPORATE MEMBERS",
  showSecondaryHeader = false,
  secondaryHeader = "OTHER USERS",
  userRoleDisplay = "text",
  roleOptions = [
    { value: "Admin", label: "Admin" },
    { value: "User", label: "User" },
    { value: "Manager", label: "Manager" },
  ],
}) => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const limit = 5;
  const { showLoader, hideLoader } = useLoader();

  const { users, isLoading } = useSelector(selectUserAdminState);

  // One org-scoped fetch on open; "View More" pages client-side to avoid 429s.
  useEffect(() => {
    if (isVisible) {
      setVisibleCount(limit);
      setSelectedMembers([]);
      setSearchQuery("");
      const organizationID = getOrganizationId();
      if (organizationID) {
        dispatch(
          fetchUsersByAny({ key: "organizationID", value: organizationID })
        );
      }
    }
  }, [isVisible, dispatch]);

  const loadMoreUsers = () => {
    setVisibleCount((prev) => prev + limit);
  };

  const handleSelectMember = (memberId) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleProceed = () => {
    if (selectedMembers.length > 0) {
      setIsDeleteModalVisible(true);
    }
  };

  const getSelectedMembersDetails = () => {
    return users
      .filter((user) => selectedMembers.includes(user._id))
      .map((user) => ({
        id: user._id,
        name: user.fullname || user.username,
        email: user.email,
        avatar: user.profileImage,
        role: user.role || "User",
      }));
  };

  const assignedIdSet = new Set(assignedUserIds);

  const filteredUsers = users
    .filter((user) => !assignedIdSet.has(user._id))
    .filter((user) => {
      const fullname = user.fullname || "";
      const username = user.username || "";
      const email = user.email || "";

      return (
        fullname.toLowerCase().includes(searchQuery.toLowerCase()) ||
        username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });

  const visibleUsers = filteredUsers.slice(0, visibleCount);
  const hasMoreUsers = filteredUsers.length > visibleCount;

  const createConfirmationMessage = () => {
    const selectedMembersDetails = getSelectedMembersDetails();
    const membersList = selectedMembersDetails
      .map(
        (member, index) =>
          `<li style="margin-bottom: 8px; font-weight: 600; color: var(--veegil-color-dark-gray)">
           ${index + 1}. ${member.name}
         </li>`
      )
      .join("");

    return `
      <div style="text-align: center;">
        <span style="color: black;">
          Are you sure you want to add the following users to this role?
        </span>
        <br/><br/>
        <ul style="list-style: none; text-align: center;">
          ${membersList}
        </ul>
      </div>
    `;
  };

  const createSuccessMessage = () => {
    const selectedMembersDetails = getSelectedMembersDetails();
    const membersList = selectedMembersDetails
      .map(
        (member, index) =>
          `<li style="margin-bottom: 8px; font-weight: 600; color: var(--veegil-color-dark-gray)">
           ${index + 1}. ${member.name}
         </li>`
      )
      .join("");

    return `
      <div style="text-align: center;">
        <span style="color: black;">
          You have successfully added:
        </span>
        <br/><br/>
        <ul style="list-style: none; text-align: center;">
          ${membersList}
        </ul>
      </div>
    `;
  };

  const handleSelectionProceed = async () => {
    if (!role || selectedMembers.length === 0) return;

    setIsDeleteModalVisible(false);
    showLoader();
    try {
      const updatedUserIDs = [
        ...new Set([...assignedUserIds, ...selectedMembers]),
      ];

      await dispatch(
        assignRoleUsersAsync({
          id: role._id,
          roleName: role.name,
          name: role.name,
          description: role.description,
          userIDs: updatedUserIDs,
          permissions: role.permissions || [],
          organizationID: getOrganizationId(),
        })
      ).unwrap();

      hideLoader();
      setSelectedMembers([]);
      onClose();
      if (onUsersAdded) {
        await onUsersAdded();
      }
      setIsSuccessModalVisible(true);
    } catch (error) {
      console.error("Failed to assign users to role:", error);
      hideLoader();
    }
  };

  const handleRoleChange = () => {};

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={onClose}
        footer={null}
        centered
        width={600}
        closeIcon={null}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden",
          },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}>
        <div className="flex flex-col bg-white">
          {/* Header */}
          <div className="p-4 px-6 flex justify-between items-center">
            <h2 className="text-bold-x text-primary">{modalTitle}</h2>
            <CloseOutlined
              onClick={onClose}
              className="cursor-pointer"
              style={{
                color: "var(--veegil-color-primary)",
                backgroundColor: "var(--veegil-color-secondary)",
                borderRadius: "50%",
                padding: "4px",
              }}
            />
          </div>

          {/* Content Area */}
          <div className="flex flex-col px-6 pb-6 space-y-6">
            {/* Search Input */}
            <Input
              placeholder="Search User"
              icon={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            {/* First User Section */}
            <div>
              <h3 className="text-darkGrey text-bold font-semibold mb-4">
                {primaryHeader}
              </h3>
              <div
                className="max-h-[400px] overflow-y-auto pr-2"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                <style>
                  {`
                    .scrollable-container::-webkit-scrollbar {
                      display: none;
                    }
                  `}
                </style>
                {isLoading && users.length === 0 ? (
                  <div>Loading users...</div>
                ) : (
                  <>
                    <div className="space-y-2 scrollable-container">
                      {visibleUsers.map((user) => (
                        <MemberCard
                          key={user._id}
                          member={user}
                          isSelected={selectedMembers.includes(user._id)}
                          onSelect={handleSelectMember}
                          userRoleDisplay={userRoleDisplay}
                          roleOptions={roleOptions}
                          onRoleChange={handleRoleChange}
                        />
                      ))}
                    </div>

                    {hasMoreUsers && (
                      <div className="mt-4 text-center">
                        {isLoading ? (
                          <div>Loading more users...</div>
                        ) : (
                          <button
                            onClick={loadMoreUsers}
                            className="text-primary font-medium hover:text-primary-dark hover:underline">
                            View More
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Divider and Second Section (conditional) */}
            {showSecondaryHeader && (
              <>
                <div className="h-[1px] bg-gray-200 w-full"></div>
                <div>
                  <h3 className="text-darkGrey text-bold font-semibold mb-4">
                    {secondaryHeader}
                  </h3>
                  <div
                    className="max-h-[400px] overflow-y-auto pr-2"
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                    {/* For now using the same data, but you might want to use different data here */}
                    <div className="space-y-2 scrollable-container">
                      {filteredUsers.map((user) => (
                        <MemberCard
                          key={`secondary-${user._id}`}
                          member={user}
                          isSelected={selectedMembers.includes(user._id)}
                          onSelect={handleSelectMember}
                          userRoleDisplay={userRoleDisplay}
                          roleOptions={roleOptions}
                          onRoleChange={handleRoleChange}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer Buttons */}
          <div className="w-full bg-secondary p-4 flex gap-3">
            <Button
              text="CANCEL"
              color="secondary"
              className="flex-1 border border-primary"
              customBgColor="white"
              customTextColor="primary"
              onClick={onClose}
            />
            <Button
              text="PROCEED"
              color="primary"
              className="flex-1 bg-primary text-white"
              customBgColor="primary"
              customTextColor="white"
              onClick={handleProceed}
              disabled={selectedMembers.length === 0}
            />
          </div>
        </div>
      </Modal>

      <Delete
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onProceed={handleSelectionProceed}
        header="Proceed with Selection?"
        paragraph={createConfirmationMessage()}
        proceedButtonText="PROCEED"
        cancelButtonText="CANCEL"
      />
      <Success
        isVisible={isSuccessModalVisible}
        onClose={() => setIsSuccessModalVisible(false)}
        header="Users Added to Role"
        paragraph={createSuccessMessage()}
        buttonText="CLOSE"
        onButtonClick={() => setIsSuccessModalVisible(false)}
      />
    </>
  );
};

MemberCard.propTypes = {
  member: PropTypes.shape({
    _id: PropTypes.string,
    fullname: PropTypes.string,
    username: PropTypes.string,
    email: PropTypes.string,
    profileImage: PropTypes.string,
    role: PropTypes.string,
  }),
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
  userRoleDisplay: PropTypes.oneOf(["text", "select"]),
  roleOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  onRoleChange: PropTypes.func,
};

AddMoreUsersDialog.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  role: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    permissions: PropTypes.arrayOf(PropTypes.string),
  }),
  assignedUserIds: PropTypes.arrayOf(PropTypes.string),
  onUsersAdded: PropTypes.func,
  modalTitle: PropTypes.string,
  primaryHeader: PropTypes.string,
  showSecondaryHeader: PropTypes.bool,
  secondaryHeader: PropTypes.string,
  userRoleDisplay: PropTypes.oneOf(["text", "select"]),
  roleOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ),
};

export default AddMoreUsersDialog;
